/**
 * @fileoverview HTTP client with interceptors for API communication
 * @module services/api
 */

import Swal from 'sweetalert2';

/**
 * Get authentication token from localStorage
 * @returns {string} Decoded token
 */
const getAuthToken = () => {
  const tokens = localStorage.getItem('mn-acc-tk') || '';
  return tokens ? atob(tokens) : '';
};

/**
 * Show alert dialog using SweetAlert2
 * @param {string} url - URL to redirect after alert
 * @param {string} icon - Alert icon type
 * @param {string} title - Alert title
 * @param {string} detail - Alert message
 * @param {string} confirm - Confirm button text
 * @param {string} cancel - Cancel button text
 * @returns {Promise}
 */
export const showAlert = (url, icon, title, detail, confirm, cancel) => {
  const modals = document.getElementsByClassName('swal2-modal');
  const icons = document.getElementsByClassName('swal2-icon');
  const hasBangIcon = Array.from(icons).some((el) => el.textContent.trim() === '!');

  if (modals.length >= 2 || hasBangIcon) return;

  return Swal.fire({
    icon: icon || 'warning',
    title: title || (icon === 'success' ? 'Success' : 'Warning'),
    text: detail || (icon === 'success' ? 'The transaction was completed successfully.' : ' '),
    confirmButtonText: confirm || 'Confirm',
    showCancelButton: !!cancel,
    cancelButtonText: cancel || 'Cancel',
    background: '#fff',
    color: '#000000',
    confirmButtonColor: '#212529',
    cancelButtonColor: 'unset',
  }).then(({ isConfirmed }) => {
    if (url) {
      if (url === '#') window.location.reload();
      else window.location.href = url;
    }
    return isConfirmed;
  });
};

/**
 * Show/hide loading spinner
 * @param {boolean} status - Show or hide loading
 */
export const loading = (status) => {
  try {
    const loadingEl = document.getElementById('loading');
    if (status === true) {
      loadingEl.classList.remove('d-none');
    } else if (status === false) {
      loadingEl.classList.add('d-none');
    }
  } catch (e) {}
};

/**
 * Remove empty strings and null values from objects/arrays recursively
 * @param {*} value - The value to clean
 * @returns {*} Cleaned value
 */
// export const removeEmptyString = (value) => {
//   if (Array.isArray(value)) {
//     const arr = value.map(removeEmptyString).filter((v) => v !== '' && v !== null);
//     return arr.length > 0 ? arr : null;
//   }

//   if (value && typeof value === 'object') {
//     const obj = Object.fromEntries(
//       Object.entries(value)
//         .map(([k, v]) => [k, removeEmptyString(v)])
//         .filter(([_, v]) => v !== '' && v !== null)
//     );
//     return Object.keys(obj).length > 0 ? obj : null;
//   }

//   return value;
// };
export const removeEmptyString = (value) => {
  // ⭐ แปลง "-" เป็น "" แต่ไม่ให้ถูกลบ
  if (value === '-') {
    return '__EMPTY__'; // ใช้ placeholder กันโดน filter
  }

  if (Array.isArray(value)) {
    const arr = value
      .map(removeEmptyString)
      .filter((v) => v !== '' && v !== null);
    
    return arr.map(v => v === '__EMPTY__' ? '' : v);
  }

  if (value && typeof value === 'object') {
    const obj = Object.fromEntries(
      Object.entries(value)
        .map(([k, v]) => [k, removeEmptyString(v)])
        .filter(([_, v]) => v !== '' && v !== null)
    );

    // แปลง placeholder กลับ
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [
        k,
        v === '__EMPTY__' ? '' : v
      ])
    );
  }

  return value;
};

/**
 * Extract error message from API response
 * @param {Object} result - API response
 * @returns {string} Error message
 */
export const ERROR = (result) => {
  loading(false);
  if (!result) return '';
  if (result.detail) return result.detail;
  if (result.message) return result.message;
  if (result.data) {
    if (result.data.message) return result.data.message;
    return result.data;
  }
  return result.message || '';
};

/**
 * Build API URL
 * @param {string} url - Endpoint URL
 * @param {Object} params - Query parameters
 * @returns {string} Full URL
 */
const buildUrl = (url, params = null) => {
  let fullUrl = url.includes('://') ? url : (process.env.REACT_APP_API_URL + url).replaceAll(' ', '');

  if (params) {
    const queryString = Object.entries(params)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    fullUrl += '?' + queryString;
  }

  return fullUrl;
};

/**
 * Handle API errors
 * @param {Object} result - API response
 * @param {Object} options - Options
 * @returns {Object} Processed response
 */
const handleResponse = (result, options = {}) => {
  if (result.status === true) return result;

  const message = ERROR(result);

  if (message === 'Token not found' || message === 'Token has expired') {
    localStorage.clear();
    showAlert('/login', 'warning', 'Warning', message);
    return { status: false, message };
  }

  if (options.skipUserVerificationAlert && message === 'User not verified, please verify your account first') {
    return { status: false, message };
  }

  if (message === 'fetch is not defined') {
    showAlert('', 'warning', 'Warning', 'Data not found');
    return { status: false, message };
  }

  showAlert('', 'warning', 'Warning', message);
  return { status: false, message };
};

/**
 * Perform GET request
 * @param {string} url - API endpoint
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} API response
 */
export const GET = async (url, params) => {
  return new Promise((resolve) => {
    const apiUrl = buildUrl(url,params);

    // สร้าง Header พื้นฐาน
    const headers = {
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: getAuthToken(),
    };

    fetch(apiUrl, {
      method: 'GET',
      headers: headers, // ใช้ header ที่เตรียมไว้
      cache: 'no-cache',
    })
      .then((result) => result.json())
      .then((result) => resolve(handleResponse(result)))
      .catch(() => resolve({ status: false, message: 'Error' }));
  });
};

/**
 * ดึงไฟล์จาก API เป็น blob — ใช้กับ endpoint ที่ตอบกลับเป็นไฟล์ ไม่ใช่ JSON
 *
 * GET() เรียก .json() ทุกครั้ง จึงใช้กับไฟล์ไม่ได้ ส่วน error ที่ API ส่งกลับมา
 * ยังเป็น JSON อยู่เหมือนเดิม ตรงนี้จึงต้องแยกอ่านตาม content-type
 *
 * ไม่เด้ง alert เองเหมือน GET() เพราะผู้เรียกมักโหลดหลายไฟล์รวดเดียว
 * ถ้าเด้งทีละไฟล์จะได้ป๊อปอัปซ้อนกันเป็นสิบใบ — ยกเว้นเคส token หมดอายุ
 * ที่ต้องเคลียร์ session แล้วส่งกลับหน้า login ทันทีเหมือน request อื่น
 *
 * @param {string} url - API endpoint
 * @param {Object} params - Query parameters
 * @returns {Promise<{status: boolean, blob?: Blob, message?: string}>}
 */
export const GET_FILE = async (url, params) => {
  try {
    const res = await fetch(buildUrl(url, params), {
      method: 'GET',
      headers: { accept: '*/*', Authorization: getAuthToken() },
      cache: 'no-cache',
    });

    const isJson = (res.headers.get('content-type') || '').includes('application/json');
    if (res.ok && !isJson) return { status: true, blob: await res.blob() };

    const result = isJson ? await res.json().catch(() => null) : null;
    const message = ERROR(result) || `HTTP ${res.status}`;

    if (message === 'Token not found' || message === 'Token has expired') {
      localStorage.clear();
      showAlert('/login', 'warning', 'Warning', message);
    }

    return { status: false, message };
  } catch (e) {
    return { status: false, message: 'Error' };
  }
};

/**
 * แปลง body เป็น JSON string สำหรับส่งขึ้น API
 *
 * ไม่มี body (null / undefined) ต้องไม่ส่งอะไรไปเลย — JSON.stringify(null) ได้สตริง
 * "null" ซึ่ง express.json() โหมด strict (ค่าตั้งต้น) ไม่รับ เพราะรับเฉพาะ object/array
 * ทำให้ endpoint ที่ไม่ต้องมี body (เช่น กดหัวใจ / ทำสำเนา) พังทั้งที่เรียกถูกแล้ว
 *
 * @param {*} body
 * @returns {string|undefined}
 */
const toRequestBody = (body) =>
  body === null || body === undefined ? undefined : JSON.stringify(removeEmptyString(body));

/**
 * Perform POST request
 * @param {string} url - API endpoint
 * @param {Object} body - Request body
 * @returns {Promise<Object>} API response
 */
export const POST = async (url, body) => {
  return new Promise((resolve) => {
    const apiUrl = buildUrl(url);

    // สร้าง Header พื้นฐาน
    const headers = {
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: getAuthToken(),
    };

    fetch(apiUrl, {
      method: 'POST',
      headers: headers, // ใช้ header ที่เตรียมไว้
      cache: 'no-cache',
      body: toRequestBody(body),
    })
      .then((result) => result.json())
      .then((result) => resolve(handleResponse(result, { skipUserVerificationAlert: true })))
      .catch(() => resolve({ status: false, message: 'Error' }));
  });
};

/**
 * Perform PUT request
 * @param {string} url - API endpoint
 * @param {Object} body - Request body
 * @returns {Promise<Object>} API response
 */
export const PUT = async (url, body) => {
  return new Promise((resolve) => {
    const apiUrl = buildUrl(url);

    // สร้าง Header พื้นฐาน
    const headers = {
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: getAuthToken(),
    };

    fetch(apiUrl, {
      method: 'PUT',
        headers: headers, // ใช้ header ที่เตรียมไว้
      cache: 'no-cache',
      body: toRequestBody(body),
    })
      .then((result) => result.json())
      .then((result) => resolve(handleResponse(result)))
      .catch(() => resolve({ status: false, message: 'Error' }));
  });
};

/**
 * Perform DELETE request
 * @param {string} url - API endpoint
 * @param {Object} body - Request body
 * @returns {Promise<Object>} API response
 */
export const DELETE = async (url, body) => {
  return new Promise((resolve) => {
    const apiUrl = buildUrl(url);

    // สร้าง Header พื้นฐาน
    const headers = {
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: getAuthToken(),
    };

    fetch(apiUrl, {
      method: 'DELETE',
      headers: headers, // ใช้ header ที่เตรียมไว้
      cache: 'no-cache',
    })
      .then((result) => result.json())
      .then((result) => resolve(handleResponse(result)))
      .catch(() => resolve({ status: false, message: 'Error' }));
  });
};

/**
 * ขนาดไฟล์สูงสุดที่อนุญาตให้อัปโหลดฝั่ง client (หน่วย: byte)
 * ใช้ตรวจก่อนอัปโหลดเพื่อกัน error 413 และให้ผู้ใช้เห็น message ที่เข้าใจง่าย
 */
export const MAX_UPLOAD_SIZE = {
  image: 15 * 1024 * 1024, // 15MB
  video: 100 * 1024 * 1024, // 100MB
  default: 50 * 1024 * 1024, // 50MB
};

/**
 * เพดานขนาดไฟล์ตามชนิด ใช้ตรวจก่อนเริ่มอัปโหลด
 * @param {File} file
 * @returns {number}
 */
const uploadLimitFor = (file) => {
  const mime = file?.type || '';
  if (mime.startsWith('image/')) return MAX_UPLOAD_SIZE.image;
  if (mime.startsWith('video/')) return MAX_UPLOAD_SIZE.video;
  return MAX_UPLOAD_SIZE.default;
};

const asMB = (bytes) => `${Math.round((bytes / (1024 * 1024)) * 10) / 10}MB`;

/**
 * ตั้งค่าการย่อรูปฝั่ง client ก่อนอัปโหลด
 *
 * รูปจากมือถือทุกวันนี้ใบละ 8–12MB ซึ่งเกินความจำเป็นสำหรับรูปประกาศมาก
 * ย่อให้เหลือด้านยาว COMPRESS_MAX_EDGE ก่อนส่ง ได้ทั้งความเร็วตอนอัปหลายรูป
 * ประหยัดพื้นที่ R2 และหน้าฝั่งลูกค้าโหลดเร็วขึ้น
 *
 */
const COMPRESS_MAX_EDGE = 2000;
const COMPRESS_QUALITY = 0.82;
// เล็กกว่านี้ไม่คุ้มที่จะ decode/encode ใหม่
const COMPRESS_SKIP_UNDER = 512 * 1024; // 512KB
// ชนิดที่ห้ามแตะ — svg เป็น vector ส่วน gif ถ้า re-encode จะเหลือเฟรมเดียว
const COMPRESS_SKIP_TYPES = ['image/svg+xml', 'image/gif'];

/**
 * แปลง canvas เป็น blob ให้ได้ทั้ง OffscreenCanvas และ canvas ปกติ
 * @returns {Promise<Blob|null>}
 */
const canvasToBlob = (canvas, type, quality) => {
  if (typeof canvas.convertToBlob === 'function') return canvas.convertToBlob({ type, quality });
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
};

/**
 * ย่อรูปด้วย canvas โดยคงชนิดไฟล์เดิมไว้
 *
 * คงชนิดเดิมเพราะ PNG ที่พื้นหลังโปร่ง (โลโก้ ลายน้ำ) จะเสียความโปร่งทันทีถ้าแปลง
 * เป็น JPEG — ลำพังการย่อขนาดก็ลดไฟล์ได้มากพออยู่แล้ว
 *
 * คืนไฟล์เดิมทุกกรณีที่ย่อไม่ได้หรือย่อแล้วไม่เล็กลง การอัปโหลดต้องไม่พังเพราะ
 * ขั้นตอนที่เป็นแค่การ optimize
 *
 * @param {File} file
 * @returns {Promise<File>}
 */
const compressImage = async (file) => {
  const type = file?.type || '';
  if (!type.startsWith('image/') || COMPRESS_SKIP_TYPES.includes(type)) return file;
  if (file.size <= COMPRESS_SKIP_UNDER) return file;
  if (typeof createImageBitmap !== 'function') return file;

  let bitmap;
  try {
    // from-image เพื่อให้รูปมือถือที่หมุนด้วย EXIF ออกมาหันถูกด้าน
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });

    const scale = Math.min(1, COMPRESS_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas =
      typeof OffscreenCanvas === 'function'
        ? new OffscreenCanvas(width, height)
        : Object.assign(document.createElement('canvas'), { width, height });

    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);

    const blob = await canvasToBlob(canvas, type, COMPRESS_QUALITY);
    // ไฟล์ที่ optimize มาดีอยู่แล้วอาจใหญ่ขึ้นหลัง re-encode — กรณีนั้นใช้ของเดิม
    if (!blob || blob.size >= file.size) return file;

    return new File([blob], file.name, { type, lastModified: file.lastModified });
  } catch (e) {
    // ย่อไม่ได้ไม่ใช่เรื่องคอขาดบาดตาย ปล่อยไฟล์เดิมไปต่อ แต่ให้เห็นใน log ว่าทำไม
    console.warn(`[UPLOAD] compress skipped for "${file.name}" — ${e?.message || e}`);
    return file;
  } finally {
    bitmap?.close?.();
  }
};

/**
 * ปิดท้ายการอัปโหลดที่ล้มเหลว — log ไว้ให้ไล่ต่อได้ แล้วคืนรูปแบบเดิมที่ผู้เรียกคาดหวัง
 *
 * ต้องมีทั้งสองทาง เพราะบางหน้าโชว์แค่ข้อความสั้น ๆ ให้ผู้ใช้ แต่คนแก้บั๊กต้องการ
 * ชื่อไฟล์กับบริบทเต็มใน console
 *
 * @param {File} file
 * @param {string} message
 */
const uploadFailed = (file, message) => {
  console.error(`[UPLOAD] upload failed for "${file?.name}" — ${message}`);
  return { status: false, message };
};

/**
 * อัปโหลดไฟล์ขึ้น R2
 *
 * 1) ย่อรูปด้วย compressImage() ทุกขั้นหลังจากนี้ทำงานกับไฟล์ที่ย่อแล้ว
 * 2) ขอ presigned URL (POST /auth/upload/presign) แล้ว PUT ไฟล์ตรงเข้า R2
 *    ไฟล์ไม่ผ่าน body ของ API เลย จึงไม่กินแรมฝั่ง server และไม่ติด limit ของ proxy
 *
 * มีทางนี้ทางเดียว ไม่มี fallback แล้ว
 *
 * ทางสำรองแบบ base64 ผ่าน POST /auth/upload ถูกถอดออกเพราะมันกลบปัญหาจริง — เวลา
 * presign พังผู้ใช้จะไม่เห็นอะไรเลยนอกจากช้าลง ไฟล์ยังไหลเข้า body ของ API เหมือนเดิม
 * และ container ก็ยังโดน OOM kill เหมือนเดิม พังให้เห็นชัดพร้อมบอกสาเหตุดีกว่า
 *
 * @param {File} file
 * @param {{ folder?: string, endpoint?: string, extra?: Object }} options
 *   endpoint : ปลายทางที่ออก presigned URL — ค่าตั้งต้นคือเส้นของผู้ใช้ที่ล็อกอินแล้ว
 *              หน้าสาธารณะที่ยังไม่มีบัญชี (ใบสมัครพาร์ตเนอร์) ส่ง "partner/upload/presign"
 *              มาแทน แล้วแนบตั๋วที่ได้จากการยืนยัน OTP ผ่าน extra
 *   extra    : ช่องเพิ่มเติมที่ต้องส่งไปกับคำขอ presign (เช่น apply_token)
 *              ไม่ใส่ folder ให้เส้นสาธารณะ — ปลายทางถูกกำหนดตายตัวฝั่ง server
 * @returns {Promise<{status: boolean, data?: {file_path, file_name, mime_type, file_size}, message?: string}>}
 */
export const UPLOAD = async (file, { folder, endpoint = 'auth/upload/presign', extra } = {}) => {
  if (!file) return { status: false, message: 'No file' };

  const limit = uploadLimitFor(file);
  if (file.size > limit) {
    return { status: false, message: `File too large (${asMB(file.size)}) — limit is ${asMB(limit)}` };
  }

  /* ย่อรูปก่อนทำอย่างอื่นทั้งหมด — ต้องเกิดก่อนขอ presign เพราะ backend เซ็น
     Content-Length ติดไปกับ URL ขนาดที่แจ้งตอนขอจึงต้องเป็นขนาดของไฟล์ที่ PUT จริง
     ตั้งแต่บรรทัดนี้ลงไปใช้ `upload` ห้ามกลับไปใช้ `file` ตัวเดิมอีก */
  const upload = await compressImage(file);

  /* ---- 1) ขอ presigned URL จาก API ----
     POST() ไม่เคย throw (มันจับ error แล้ว resolve เป็น {status:false} เสมอ)
     จึงเช็กจากค่าที่คืนมาอย่างเดียว ไม่ต้องมี try/catch ครอบ

     ข้อความจาก API มีค่ามาก เช่น "R2 storage is not configured — missing ..."
     ที่บอกได้ทันทีว่าเป็นเรื่อง env ฝั่ง server — ส่งต่อให้ผู้ใช้เห็นตรง ๆ */
  const presign = await POST(endpoint, {
    file_name: upload.name,
    mime_type: upload.type || undefined,
    folder,
    file_size: upload.size,
    ...(extra || {}),
  });

  if (presign?.status !== true || !presign?.data?.upload_url) {
    return uploadFailed(upload, presign?.message || 'presign request rejected');
  }

  // ---- 2) PUT ไฟล์ตรงเข้า R2 ----
  const { upload_url, public_url, mime_type, content_disposition } = presign.data;
  const putHeaders = { 'Content-Type': mime_type || upload.type || 'application/octet-stream' };
  // ต้องส่งค่าตรงกับที่ backend เซ็นไว้ใน presigned URL ไม่งั้น R2 จะตอบ 403 SignatureDoesNotMatch
  // Content-Length ก็ถูกเซ็นไว้เหมือนกัน แต่เบราว์เซอร์ตั้งให้เองจากขนาด body
  // จึงต้องส่ง `upload` ตัวเดิมที่แจ้งขนาดไว้ตอนขอ presign เท่านั้น
  if (content_disposition) putHeaders['Content-Disposition'] = content_disposition;

  let putRes;
  try {
    putRes = await fetch(upload_url, { method: 'PUT', headers: putHeaders, body: upload });
  } catch (e) {
    /* fetch โยน TypeError = คำขอไม่เคยออกจากเบราว์เซอร์ ไม่ใช่ว่า R2 ตอบ error
       PUT ชุดนี้แนบ Content-Disposition ซึ่งไม่ใช่ CORS-safelisted header
       จึงต้องผ่าน preflight OPTIONS ก่อนเสมอ ถ้าบักเก็ตยังไม่มี CORS policy
       เบราว์เซอร์จะบล็อกตั้งแต่ preflight แล้วโผล่มาเป็น "Failed to fetch" ลอย ๆ

       เคยเขียนข้อความว่า "presign request error" ซึ่งชี้ผิดจุดสนิท — presign
       ผ่านไปแล้วตั้งแต่ขั้นก่อนหน้า ปัญหาอยู่ที่บักเก็ตล้วน ๆ */
    return uploadFailed(upload, `blocked before reaching R2 (${e?.message || e}) — the bucket needs a CORS policy allowing PUT from this origin`);
  }

  if (!putRes.ok) {
    // ออกจากเบราว์เซอร์ได้แล้วแต่ R2 ปฏิเสธ — เป็นเรื่อง credential/bucket/ลายเซ็น ไม่ใช่ CORS
    return uploadFailed(upload, `R2 rejected the upload (HTTP ${putRes.status}) — check R2 credentials, bucket name and endpoint`);
  }

  return {
    status: true,
    data: {
      file_path: public_url,
      file_name: upload.name || null,
      mime_type: mime_type || upload.type || null,
      file_size: upload.size ?? null,
    },
  };
};

const api = {
  GET,
  POST,
  PUT,
  DELETE,
  UPLOAD,
  ERROR,
  loading,
  showAlert,
  removeEmptyString,
  MAX_UPLOAD_SIZE,
};

export default api;
